import { useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/lib/safeToast";
import { Plus, Save, Trash2, History, AlertTriangle } from "lucide-react";

type Hypothesis = {
  id: string;
  diagnostic_label: string;
  dsm5_tr_label: string | null;
  cid11_code: string | null;
  status: string;
  confidence_0_100: number | null;
  structured_payload: Record<string, any> | null;
  source_basis: string[] | null;
  review_required: boolean;
  deleted_at?: string | null;
};

const empty = {
  diagnostic_label: "",
  dsm5_tr_label: "",
  cid11_code: "",
  status: "working",
  confidence_0_100: 50,
  source_basis: [] as string[],
  review_required: true,
  structured_payload: { rationale: "", supporting_evidence: "", contradictory_evidence: "", falsifiers: "", next_review: "" },
};

const statusOptions = [
  ["working", "Em formulação"],
  ["probable", "Provável"],
  ["confirmed", "Confirmado pelo médico"],
  ["ruled_out", "Afastado"],
  ["deferred", "Adiado / dados insuficientes"],
];

export function DiagnosticWorkbench({ patientId }: { patientId: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<any>(empty);
  const [editing, setEditing] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState<Record<string, string>>({});
  const [showHistory, setShowHistory] = useState<string | null>(null);

  const { data: hypotheses = [], isLoading, error } = useQuery({
    queryKey: ["diagnostic-hypotheses", user?.id, patientId],
    enabled: !!user?.id && !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("diagnostic_hypotheses").select("*").eq("owner_id", user?.id).eq("patient_id", patientId).is("deleted_at", null).order("updated_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Hypothesis[];
    },
  });

  const { data: revisions = [] } = useQuery({
    queryKey: ["diagnostic-hypothesis-revisions", user?.id, showHistory],
    enabled: !!user?.id && !!showHistory,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("diagnostic_hypothesis_revisions").select("*").eq("owner_id", user?.id).eq("hypothesis_id", showHistory).order("created_at", { ascending: false }).limit(20);
      if (error) throw error;
      return data ?? [];
    },
  });

  const updateDraft = (key: string, value: any) => setDraft((prev: any) => ({ ...prev, [key]: value }));
  const startEdit = (h: Hypothesis) => {
    setEditing(h.id);
    setDraft({
      diagnostic_label: h.diagnostic_label ?? "",
      dsm5_tr_label: h.dsm5_tr_label ?? "",
      cid11_code: h.cid11_code ?? "",
      status: h.status ?? "working",
      confidence_0_100: h.confidence_0_100 ?? 50,
      source_basis: h.source_basis ?? [],
      review_required: h.review_required ?? true,
      structured_payload: {
        rationale: h.structured_payload?.rationale ?? "",
        supporting_evidence: h.structured_payload?.supporting_evidence ?? "",
        contradictory_evidence: h.structured_payload?.contradictory_evidence ?? "",
        falsifiers: h.structured_payload?.falsifiers ?? "",
        next_review: h.structured_payload?.next_review ?? "",
      },
    });
  };

  async function save() {
    if (!user?.id) return toast.error("Sessão expirada. Entre novamente.");
    if (!draft.diagnostic_label.trim()) return toast.error("Informe o nome da hipótese diagnóstica.");
    const payload = {
      owner_id: user.id,
      patient_id: patientId,
      diagnostic_label: draft.diagnostic_label.trim(),
      dsm5_tr_label: draft.dsm5_tr_label.trim() || null,
      cid11_code: draft.cid11_code.trim() || null,
      status: draft.status,
      confidence_0_100: Math.max(0, Math.min(100, Number(draft.confidence_0_100) || 0)),
      source_basis: draft.source_basis,
      review_required: draft.review_required,
      structured_payload: draft.structured_payload,
      updated_at: new Date().toISOString(),
    };
    if (editing) {
      const previous = hypotheses.find((h) => h.id === editing);
      const { error: revisionError } = await (supabase as any).from("diagnostic_hypothesis_revisions").insert({
        owner_id: user.id,
        hypothesis_id: editing,
        revision_payload: previous ?? {},
        reason: "Snapshot automático antes da edição; registrar justificativa clínica no próximo campo de revisão.",
      });
      if (revisionError) return toast.error("Não foi possível criar histórico da hipótese: " + revisionError.message);
      const { error } = await (supabase as any).from("diagnostic_hypotheses").update(payload).eq("id", editing).eq("owner_id", user.id).eq("patient_id", patientId);
      if (error) return toast.error(error.message);
      toast.success("Hipótese atualizada; versão anterior preservada.");
    } else {
      const { error } = await (supabase as any).from("diagnostic_hypotheses").insert(payload);
      if (error) return toast.error(error.message);
      toast.success("Hipótese adicionada.");
    }
    setEditing(null);
    setDraft(empty);
    await qc.invalidateQueries({ queryKey: ["diagnostic-hypotheses", user.id, patientId] });
  }

  async function archive(h: Hypothesis) {
    if (!user?.id) return toast.error("Sessão expirada. Entre novamente.");
    const reason = (deleteReason[h.id] ?? "").trim();
    if (!reason) return toast.error("Registre a justificativa antes de afastar/arquivar a hipótese.");
    const { error: revisionError } = await (supabase as any).from("diagnostic_hypothesis_revisions").insert({
      owner_id: user.id,
      hypothesis_id: h.id,
      revision_payload: h,
      reason: "Hipótese arquivada/afastada: " + reason,
    });
    if (revisionError) return toast.error("Não foi possível preservar a versão anterior: " + revisionError.message);
    const { error } = await (supabase as any).from("diagnostic_hypotheses").update({
      status: "ruled_out",
      deleted_at: new Date().toISOString(),
      deleted_by: user.id,
      delete_reason: reason,
      review_required: false,
      updated_at: new Date().toISOString(),
    }).eq("id", h.id).eq("owner_id", user.id).eq("patient_id", patientId);
    if (error) return toast.error(error.message);
    toast.success("Hipótese arquivada com justificativa e histórico.");
    await qc.invalidateQueries({ queryKey: ["diagnostic-hypotheses", patientId] });
  }

  return (
    <Card className="p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold">Diagnósticos diferenciais e hipóteses</h3>
          <p className="text-sm text-muted-foreground mt-1">Edite status, CID-11, confiança, evidências favoráveis/contrárias e critérios que enfraqueceriam cada hipótese. Alterações mantêm histórico.</p>
        </div>
        <Badge variant="outline">Decisão médica obrigatória</Badge>
      </div>
      {isLoading ? <p className="text-sm text-muted-foreground">Carregando hipóteses…</p> : null}
      {error ? <p role="alert" className="text-sm text-destructive">Falha ao carregar hipóteses: {(error as Error).message}</p> : null}
      {!isLoading && !error && hypotheses.length === 0 ? <p className="text-sm text-muted-foreground">Ainda não há hipóteses estruturadas. Os diagnósticos gerais do cadastro não serão apagados; adicione diferenciais abaixo.</p> : null}
      <div className="space-y-3">
        {hypotheses.map((h) => (
          <div key={h.id} className="rounded-xl border p-3 space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium">{h.diagnostic_label}</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <Badge variant="secondary">{statusOptions.find(([value]) => value === h.status)?.[1] ?? h.status}</Badge>
                  {h.cid11_code ? <Badge variant="outline">CID-11 {h.cid11_code}</Badge> : null}
                  <Badge variant="outline">Confiança: {h.confidence_0_100 ?? "—"}%</Badge>
                  {h.review_required ? <Badge variant="outline">Revisão pendente</Badge> : null}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => { setShowHistory(h.id); startEdit(h); }}>Editar</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowHistory(showHistory === h.id ? null : h.id)}><History className="h-4 w-4 mr-1" />Histórico</Button>
              </div>
            </div>
            {h.structured_payload?.rationale ? <p className="text-sm text-muted-foreground">{h.structured_payload.rationale}</p> : null}
            {editing !== h.id ? (
              <div className="grid md:grid-cols-[1fr_auto] gap-2 pt-1">
                <Input aria-label="Justificativa para afastar hipótese" placeholder="Justificativa clínica para afastar/arquivar…" value={deleteReason[h.id] ?? ""} onChange={(e) => setDeleteReason((old) => ({ ...old, [h.id]: e.target.value }))} />
                <Button size="sm" variant="outline" onClick={() => archive(h)}><Trash2 className="h-4 w-4 mr-1" />Afastar com justificativa</Button>
              </div>
            ) : null}
            {showHistory === h.id ? <div className="rounded-lg bg-muted/30 p-3 text-xs space-y-2">{revisions.length ? revisions.map((r: any) => <div key={r.id} className="border-b pb-2 last:border-0"><div className="font-medium">{new Date(r.created_at).toLocaleString("pt-BR")}</div><div>{r.reason}</div><pre className="mt-1 whitespace-pre-wrap break-words">{JSON.stringify(r.revision_payload, null, 2)}</pre></div>) : <p className="text-muted-foreground">Nenhuma revisão anterior registrada.</p>}</div> : null}
          </div>
        ))}
      </div>
      <div className="border-t pt-4 space-y-3">
        <h4 className="font-medium">{editing ? "Editar hipótese" : "Adicionar hipótese / diferencial"}</h4>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Diagnóstico ou hipótese *"><Input value={draft.diagnostic_label} onChange={(e) => updateDraft("diagnostic_label", e.target.value)} placeholder="Ex.: episódio depressivo; efeito induzido por substância…" /></Field>
          <Field label="CID-11"><Input value={draft.cid11_code} onChange={(e) => updateDraft("cid11_code", e.target.value)} placeholder="Código e especificador" /></Field>
          <Field label="DSM-5-TR / formulação"><Input value={draft.dsm5_tr_label} onChange={(e) => updateDraft("dsm5_tr_label", e.target.value)} /></Field>
          <Field label="Status"><select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={draft.status} onChange={(e) => updateDraft("status", e.target.value)}>{statusOptions.map(([v, label]) => <option key={v} value={v}>{label}</option>)}</select></Field>
          <Field label={`Confiança clínica estimada: ${draft.confidence_0_100}%`}><Input type="range" min="0" max="100" value={draft.confidence_0_100} onChange={(e) => updateDraft("confidence_0_100", Number(e.target.value))} /></Field>
          <Field label="Revisão médica obrigatória"><select className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={String(draft.review_required)} onChange={(e) => updateDraft("review_required", e.target.value === "true")}><option value="true">Sim — não consolidado</option><option value="false">Não — já revisado</option></select></Field>
        </div>
        <Field label="Racional clínico / por que agora"><Textarea value={draft.structured_payload.rationale} onChange={(e) => updateDraft("structured_payload", { ...draft.structured_payload, rationale: e.target.value })} rows={3} /></Field>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Evidências a favor"><Textarea value={draft.structured_payload.supporting_evidence} onChange={(e) => updateDraft("structured_payload", { ...draft.structured_payload, supporting_evidence: e.target.value })} rows={3} /></Field>
          <Field label="Evidências contrárias / falsificadores"><Textarea value={draft.structured_payload.contradictory_evidence} onChange={(e) => updateDraft("structured_payload", { ...draft.structured_payload, contradictory_evidence: e.target.value })} rows={3} /></Field>
          <Field label="O que precisa ser investigado"><Textarea value={draft.structured_payload.falsifiers} onChange={(e) => updateDraft("structured_payload", { ...draft.structured_payload, falsifiers: e.target.value })} rows={2} /></Field>
          <Field label="Fontes de evidência"><Input value={(draft.source_basis ?? []).join(", ")} onChange={(e) => updateDraft("source_basis", e.target.value.split(",").map((s: string) => s.trim()).filter(Boolean))} placeholder="check-in, sessão, escala, PDF, resposta terapêutica" /></Field>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={save}><Save className="h-4 w-4 mr-1" />Salvar hipótese</Button>
          {editing ? <Button variant="outline" onClick={() => { setEditing(null); setDraft(empty); }}>Cancelar edição</Button> : <Button variant="outline" onClick={() => { setDraft(empty); }}><Plus className="h-4 w-4 mr-1" />Limpar formulário</Button>}
        </div>
        <p className="text-xs text-muted-foreground flex gap-2"><AlertTriangle className="h-4 w-4 shrink-0" />O sistema pode sugerir e reordenar diferenciais, mas não deve confirmar nem apagar diagnósticos automaticamente. Afastamento exige justificativa clínica e deixa trilha de auditoria.</p>
      </div>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}
